import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceDataSource,
    LsqApiServiceListSource,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    BUYER_ACCOUNT_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_BUYER_PAYMENTS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { Money } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    GetSellerInvoiceExportRequest,
    InvoiceBuyerApprovalStatus,
    InvoiceExportType,
    InvoiceFundingStatus,
    InvoiceSummary,
    ListInvoiceSummaryForSellerRequest,
    RedactedBankAccount
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    ListAllSupplierNamesAndIdsRequest,
    ListAllSupplierNamesAndIdsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/invoice-list/list-all-supplier-names-and-ids';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    FacilityUtilizationLegacyRequest,
    FacilityUtilizationLegacyResponse,
    GetFacilityUtilizationPayLater,
    InvoiceSummary2,
    ListGroupedInvoicePayLaterEligibleForBuyerService,
    ListInvoicePayLaterEligibleForBuyerRequest,
    ListInvoicePayLaterEligibleForBuyerResponse,
    ListInvoicePayLaterEligibleForBuyerService,
    PayableStatus
} from '@lsq-frontend/remote-services/financing-graphql';
import { ListGroupedInvoicePayLaterEligibleForBuyerRequest } from '@lsq-frontend/remote-services/financing-graphql/src/lib/buyer-account/pay-later/request/list-grouped-invoice-pay-later-eligible-for-buyer-request';
import {
    AcceptOfferRequest,
    AcceptOfferResponse,
    AcceptOfferService,
    PayableRequest
} from '@lsq-frontend/remote-services/funding-api';
import { PayLaterInvoiceExportService } from '@lsq-frontend/remote-services/report-center';
import {
    BankAccountTypeDisplay,
    ClassNames,
    Color,
    DateFormat,
    DatePickerType,
    forLifeOf,
    getDynamicRouterLink,
    Interaction,
    LifecycleAware,
    Modal,
    TableAppearance,
    TableHelper
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    ErrorMessageHelper,
    FeatureFlagService,
    getRouterLinkById,
    getRouterLinkUrlById,
    Icons,
    LsqDatePipe,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { LsqInputComponent, LsqMultiSelectComponent } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { CheckableRowsServiceFactory } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, from, Observable, ReplaySubject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    skip,
    startWith,
    tap
} from 'rxjs/operators';
import { TAKE_ALL } from '../../../constants';
import { LoadableInvoiceSummary, mapInvoicesToTotal } from '../../seller/invoices/invoices.helpers';
import { ScfSellerService } from '../../seller/services/scf-seller.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain-direct-buyer-account-shared.service';
import { RequestPayLaterConfirmationModalComponent } from './request-pay-later-confirmation-modal/request-pay-later-confirmation-modal.component';

@LifecycleAware()
@Component({
    selector: 'request-early-payment-page',
    templateUrl: './request-pay-later-page.component.html',
    styleUrls: ['./request-pay-later-page.component.scss'],
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({
                    transform: 'translateY(-100%)  scaleY(0)'
                }),
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        transform: 'translateY(0) scaleY(1)'
                    })
                )
            ])
        ])
    ],
    encapsulation: ViewEncapsulation.None
})
export class RequestPayLaterPageComponent implements OnInit {
    @ViewChild(LsqMultiSelectComponent)
    public sellerKeysMultiSelectComponent!: LsqMultiSelectComponent;
    @ViewChild('invoiceNumberSearch', { read: LsqInputComponent })
    public invoiceNumberSearchCmp!: LsqInputComponent;

    public readonly supplierNameI18n = this._i18n({
        value: 'Supplier',
        meaning: `Seller's name`,
        description: 'Table column header'
    });
    public readonly invoiceNumberI18n = this._i18n({
        value: 'Invoice Number',
        meaning: `Invoice number`,
        description: 'Table column header'
    });
    public readonly invoiceDateI18n = this._i18n({
        value: 'Invoice Date',
        meaning: `Invoice date`,
        description: 'Table column header'
    });
    public readonly dueDateI18n = this._i18n({
        value: 'Due Date',
        meaning: `Date the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly dueInI18n = this._i18n({
        value: 'Due In',
        meaning: `The day to flex term`,
        description: 'Table column header'
    });
    public readonly invoiceAmountI18n = this._i18n({
        value: 'Invoice Amount',
        meaning: `The money value of the invoice`,
        description: 'Table column header'
    });
    public readonly payLaterTermI18n = this._i18n({
        value: 'Max FlexTerm',
        meaning: ``,
        description: 'Table column header'
    });
    public readonly newDueDateI18n = this._i18n({
        value: 'New Due Date',
        meaning: `The new Date the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly utilizedI18n = this._i18n({
        value: 'UTILIZED',
        meaning: `Utilized amount of flex term invoice`,
        description: 'Progress bar labels'
    });
    public readonly availableI18n = this._i18n({
        value: 'AVAILABLE',
        meaning: `Available amount of flex term invoice`,
        description: 'Progress bar labels'
    });
    public readonly facilityLimitI18n = this._i18n({
        value: 'FACILITY LIMIT',
        meaning: `Limit amount of flex term invoice`,
        description: 'Progress bar labels'
    });
    public readonly totalPaymentI18n = this._i18n({
        value: `TOTAL PAYMENTS REQUEST`,
        meaning: `Total payment request for flex term`,
        description: 'Payment later labels'
    });
    public readonly confirmationI18n = this._i18n({
        value: `Confirm all details are correct`,
        meaning: `Confirm all details are correct`,
        description: 'Confirm message'
    });
    public paymentConfirmationTitle = this._i18n({
        value: 'FlexTerm Request Confirmation',
        meaning: 'Request FlexTerm Confirmation modal title'
    });
    public backToPayLaterRequestButton = this._i18n({
        value: 'Back to Request FlexTerm',
        meaning: 'Back to Request FlexTerm page button'
    });
    public okButtonText = this._i18n('View All Processed Payments');
    public readonly allSuppliersI18n = this._i18n('All Suppliers');
    public readonly selectSupplierI18n = this._i18n('Select Supplier');
    public readonly noDataTitle = this._i18n(
        `You currently have no invoices available for flex term.`
    );
    public readonly noDataDescription = this._i18n(
        `Invoices uploaded and approved by your buyers will appear here when they are eligible for flex term.`
    );
    public readonly invoiceNumberLabel = this._i18n({
        value: 'Invoice #',
        meaning: 'Invoice # column header'
    });
    public readonly dateSelectLabel = this._i18n('Due:');
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly invoiceFundingStatus = InvoiceFundingStatus;
    public readonly featureFlags = FeatureFlags;
    public readonly tableAppearance = TableAppearance;
    public readonly datePickerType = DatePickerType;
    public readonly rowIsUncheckableBound = this.rowIsUncheckable.bind(this);
    public readonly openInvoiceDetailsBound = this._openInvoiceDetail.bind(this);
    public readonly getRowClassNameBound = this._getRowClassName.bind(this);
    public readonly isInvoiceClickableBound = this.isInvoiceClickable.bind(this);
    public readonly rowPrimaryKey = 'refInvoiceKey';
    public readonly debounceTime = Interaction.SearchDebounceTimeMs;
    public readonly dateFormat = DateFormat;
    public readonly feeRatePercentDigitInfo = '1.2-5';
    public isLoading = true;
    private _isOptedInForAllPaymentsSubject = new BehaviorSubject<boolean>(false);
    public isOptedInForAllPayments$ = this._isOptedInForAllPaymentsSubject.asObservable();
    public readonly today = new Date();
    public startDate: string;
    public endDate: string;
    public searchInvoicesFormGroup = new FormGroup({
        invoiceNumberSearch: new FormControl('')
    });
    public dateFilterFormGroup = new FormGroup({
        dateFilter: new FormControl(undefined)
    });
    public sellersFormGroup = new FormGroup({
        sellerKeys: new FormControl([])
    });
    public defaultBankAccount: RedactedBankAccount;
    public bankAccounts$: ReplaySubject<RedactedBankAccount[]>;
    public tableDataSource: LsqApiServiceListSource<
        ListInvoicePayLaterEligibleForBuyerResponse,
        ListInvoicePayLaterEligibleForBuyerRequest,
        InvoiceSummary2
    >;
    public readonly approvalStatuses = [InvoiceBuyerApprovalStatus.APPROVED];
    public readonly expandedGroupInvoiceKeys = new Set<string>();
    public readonly checkableRowsService = this._checkableRowsServiceFactory.create();
    private readonly _selectedInvoiceKeys$ = new BehaviorSubject<Guid[]>([]);
    public readonly allSelectableInvoices$ = new ReplaySubject<InvoiceSummary2[]>(1);
    public readonly pageSelectableInvoices$ = new ReplaySubject<InvoiceSummary2[]>(1);
    public invoiceNumberSearchChange$: Observable<string>;
    public isFinancingAccountPortalUser = false;
    public invoiceGroupTogglesAreDisabled = false;
    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 15;
            }

            if (dimensions.width > 1375) {
                return 12;
            }

            if (dimensions.width > 990) {
                return 8;
            }
            return 10;
        })
    );
    public reportName = `Invoices_${this._lsqDatePipe
        .transform(new Date(), 'shortDate')
        .replace(/\//g, '-')}`;
    public agree: boolean;
    public readonly listSupplierNamesDataSource = new LsqApiServiceDataSource(
        this._listAllSupplierNamesAndIdsService,
        new ListAllSupplierNamesAndIdsRequest({
            buyerKeysFilter: []
        })
    );
    public readonly supplierFilterOptions$ = this.listSupplierNamesDataSource.data$.pipe(
        map((response) =>
            response.supplierNames!.map((sellerNameAndId) => ({
                label: sellerNameAndId.name!,
                value: sellerNameAndId.sellerKey!
            }))
        )
    );
    public readonly allSelectedInvoices$ = combineLatest([
        this.allSelectableInvoices$,
        this._selectedInvoiceKeys$
    ]).pipe(
        map(([allSelectableInvoices, selectedInvoiceKeys]) =>
            allSelectableInvoices.filter((selectableInvoice) =>
                selectedInvoiceKeys.includes(selectableInvoice.refInvoiceKey)
            )
        ),
        shareReplay(1)
    );
    public utilizedAmount$: Observable<Money>;
    public utilizedAmountPercentage: number;
    public totalInvoices: number;
    public availableAmount$: Observable<Money>;
    public facilityLimitAmount$: Observable<Money>;
    public facilityLimitAmount: Money;
    public totalSelectedAvailableAmount$: Observable<Money>;
    public totalSelectedFeeAmount$: Observable<Money>;
    public newTotalSelectedAmountDue$: Observable<Money>;
    public submittingBatch = false;
    public facilityUtilization: Observable<FacilityUtilizationLegacyResponse>;
    public editInvoicesClaimIsOn$ = this._claimService.isOn(
        DashboardClaim.FinancingBuyerPortalEditInvoices
    );

    constructor(
        public responsiveService: ResponsiveService,
        public windowReferenceService: WindowReferenceService,
        public bankAccountTypeDisplay: BankAccountTypeDisplay,
        public payLaterInvoiceExportService: PayLaterInvoiceExportService,
        private _listInvoicePayLaterEligibleForBuyerService: ListInvoicePayLaterEligibleForBuyerService,
        private _listGroupedInvoicePayLaterEligibleForBuyerService: ListGroupedInvoicePayLaterEligibleForBuyerService,
        private _listAllSupplierNamesAndIdsService: ListAllSupplierNamesAndIdsService,
        private _getFacilityUtilizationPayLater: GetFacilityUtilizationPayLater,
        private _acceptOffer: AcceptOfferService,
        private _modalService: ModalService,
        private _alertService: AlertService,
        private _errorMessageHelper: ErrorMessageHelper,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _claimService: ClaimService,
        private _checkableRowsServiceFactory: CheckableRowsServiceFactory,
        private _scfSellerService: ScfSellerService,
        private _userService: UserService,
        private _i18n: I18n,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _featureFlagService: FeatureFlagService,
        private _lsqDatePipe: LsqDatePipe
    ) {}

    public async ngOnInit(): Promise<void> {
        this.supplierFilterOptions$.pipe(forLifeOf(this)).subscribe();
        this.checkableRowsService.setLoading(true);
        this.bankAccounts$ = this._scfSellerService.bankAccounts$;
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );

        this._initTable();

        this.tableDataSource.data$.subscribe(() => {
            this.setFilterQueryParams();
        });
        this.tableDataSource.rows$
            .pipe(
                forLifeOf(this),
                map((invoices) => invoices.filter(this._isCheckable))
            )
            .subscribe((checkableInvs) => {
                this.setPageSelectableInvoices(checkableInvs);
                this.checkableRowsService.setCurrentPageKeys(
                    checkableInvs.map((invoice) => invoice.refInvoiceKey)
                );
            });

        this.invoiceNumberSearchChange$ = this.searchInvoicesFormGroup.controls.invoiceNumberSearch.valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(this.debounceTime),
            shareReplay(1)
        );
        this.invoiceNumberSearchChange$
            .pipe(forLifeOf(this), distinctUntilChanged(), shareReplay(1))
            .subscribe((invoiceNumberSearch) => {
                this.expandedGroupInvoiceKeys.clear();
                this.tableDataSource.dispatchRequest({
                    ...this.tableDataSource.request,
                    payableNameSearch: invoiceNumberSearch,
                    sellerKeys: [],
                    pagination: {
                        ...this.tableDataSource.request.pagination,
                        skip: 0
                    }
                });
            });
        this.invoiceNumberSearchChange$.subscribe(() => {
            this.tableDataSource.rows$
                .pipe(
                    forLifeOf(this),
                    skip(1),
                    first(),
                    filter((invoiceRows) =>
                        invoiceRows.some((invoiceRow) => !!invoiceRow.groupedInvoiceNumberMatched)
                    )
                )
                .subscribe(async (invoiceRows) => {
                    const filteredRows = invoiceRows.filter(
                        (invoiceRow) => invoiceRow.groupedInvoiceNumberMatched
                    );
                    for (const row of filteredRows) {
                        await this._expandGroupByInvoiceKey(row.refInvoiceKey);
                    }
                });
        });

        this.dateFilterFormGroup.controls.dateFilter.valueChanges
            .pipe(forLifeOf(this), distinctUntilChanged(), startWith(undefined))
            .subscribe((dateFilter) => {
                if (dateFilter) {
                    const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
                    const sellerKeys = queryParamMap.getAll(
                        FastTrackRouteQueryParams.SupplierKeysFilter
                    );
                    this.tableDataSource.dispatchRequest({
                        ...this.tableDataSource.request,
                        sellerKeys: sellerKeys ?? [],
                        pagination: {
                            ...this.tableDataSource.request.pagination,
                            skip: 0
                        },
                        dueDateRange: {
                            startDate: dateFilter.start.toISOString().split('T')[0],
                            endDate: dateFilter.end.toISOString().split('T')[0]
                        }
                    });
                }
            });

        this.sellersFormGroup.controls.sellerKeys.valueChanges
            .pipe(
                forLifeOf(this),
                debounceTime(this.debounceTime),
                filter(
                    (sellerKeys) => !isEqual(sellerKeys, this.tableDataSource.request.sellerKeys)
                )
            )
            .subscribe((sellerKeys) => {
                this.tableDataSource.dispatchRequest({
                    ...this.tableDataSource.request,
                    sellerKeys: sellerKeys ?? [],
                    pagination: {
                        ...this.tableDataSource.request.pagination,
                        skip: 0
                    }
                });
            });

        this.checkableRowsService.checkedRowsSubject
            .pipe(forLifeOf(this))
            .subscribe((checkedRowKeys) => {
                this.setInvoiceKeysSelected(checkedRowKeys as Guid[]);
                this.agree = false;
            });

        this.tableDataSource.request$
            .pipe(
                distinctUntilChanged((before, after) => {
                    // Ignore pagination here — only reset if the filtering has changed.
                    const { pagination: _pagBefore, ...filtersBefore } = { ...before };
                    const { pagination: _pagAfter, ...filtersAfter } = { ...after };
                    return isEqual(filtersBefore, filtersAfter);
                })
            )
            .subscribe((req) => {
                this.checkableRowsService.uncheckAll();
                this.checkableRowsService.uncheckAllFiltered();
                this.setAllSelectableInvoices(req);
                this.agree = false;
            });

        this.facilityUtilization = from(
            this._getFacilityUtilizationPayLater.executeOnce(
                new FacilityUtilizationLegacyRequest({
                    refBuyerKeys: [this._getAlternateCompanyKey()]
                })
            )
        );

        // Total request amounts
        this.totalSelectedAvailableAmount$ = mapInvoicesToTotal(
            this.allSelectedInvoices$,
            'invoiceAmount'
        );
        this.totalSelectedFeeAmount$ = mapInvoicesToTotal(
            this.allSelectedInvoices$,
            'payLaterFee' as keyof InvoiceSummary
        );
        this.newTotalSelectedAmountDue$ = combineLatest(
            [this.totalSelectedFeeAmount$, this.totalSelectedAvailableAmount$],
            (totalFeeAmountSelected, totalAvailableAmountSelected) => {
                return new Money({
                    cents: totalFeeAmountSelected.cents + totalAvailableAmountSelected.cents,
                    currencyCode: totalFeeAmountSelected.currencyCode
                });
            }
        );
        // Amounts from server
        this.utilizedAmount$ = combineLatest(
            [this.facilityUtilization, this.totalSelectedAvailableAmount$],
            (utilization, totalSelected) => {
                const utilized: Money = utilization.utilized;
                return new Money({
                    cents: utilized.cents + totalSelected.cents,
                    currencyCode: utilized.currencyCode
                });
            }
        );
        this.availableAmount$ = combineLatest([
            this.facilityUtilization,
            this.totalSelectedAvailableAmount$
        ]).pipe(
            map(([facilityUtilization, totalSelected]) => {
                const available: Money = facilityUtilization.available;
                return new Money({
                    cents: available.cents - totalSelected.cents,
                    currencyCode: available.currencyCode
                });
            })
        );
        this.facilityLimitAmount$ = this.facilityUtilization.pipe(
            map((utilization) => utilization.limit),
            tap((utilization) => (this.facilityLimitAmount = utilization))
        );
        combineLatest([this.utilizedAmount$, this.facilityLimitAmount$])
            .pipe(forLifeOf(this))
            .subscribe(([utilized, limit]) => {
                this.utilizedAmountPercentage = (utilized.cents / limit.cents) * 100;
            });
        this._selectedInvoiceKeys$.subscribe(
            (invoicesKeys) => (this.totalInvoices = invoicesKeys.length)
        );

        this.isLoading = false;
    }

    public ngOnDestroy(): void {}

    private _initTable(): void {
        const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
        const pagination = TableHelper.getPaginationByQueryParams(
            this._activatedRoute.snapshot.queryParamMap
        );
        const invoiceNumberSearch = queryParamMap.get(FastTrackRouteQueryParams.SearchText);
        if (invoiceNumberSearch) {
            this.searchInvoicesFormGroup.patchValue(
                {
                    invoiceNumberSearch: invoiceNumberSearch
                },
                {
                    onlySelf: true,
                    emitEvent: false
                }
            );
        }
        const sellerKeys = queryParamMap.getAll(FastTrackRouteQueryParams.SupplierKeysFilter);
        if (sellerKeys) {
            this.sellersFormGroup.patchValue(
                {
                    sellerKeys: sellerKeys
                },
                {
                    onlySelf: true,
                    emitEvent: false
                }
            );
        }
        this.startDate = queryParamMap.get(FastTrackRouteQueryParams.StartDate) || '';
        this.endDate = queryParamMap.get(FastTrackRouteQueryParams.EndDate) || '';
        if (this.startDate || this.endDate) {
            this.dateFilterFormGroup.patchValue(
                {
                    dateFilter: {
                        start: this.startDate,
                        end: this.endDate
                    }
                },
                {
                    onlySelf: true,
                    emitEvent: false
                }
            );
        }
        if (this.isFinancingAccountPortalUser) {
            throw new Error('This feature is not yet implemented for account-level users');
        } else {
            const initialRequest = new ListInvoicePayLaterEligibleForBuyerRequest({
                refBuyerKey: this._getAlternateCompanyKey(),
                sellerKeys: sellerKeys,
                payableNameSearch: (invoiceNumberSearch as any) ?? '',
                buyerInvoiceStatus: [
                    PayableStatus.APPROVED,
                    PayableStatus.EARLY_PAID,
                    PayableStatus.EARLY_PAY_REQUESTED
                ],
                dueDateRange: {
                    startDate: this.dateFilterFormGroup.value.dateFilter?.start
                        ?.toISOString()
                        .split('T')[0],
                    endDate: this.dateFilterFormGroup.value.dateFilter?.end
                        ?.toISOString()
                        .split('T')[0]
                },
                pagination: {
                    ...new Pagination(),
                    ...pagination
                } as Pagination
            });
            if (!this.tableDataSource) {
                this.tableDataSource = new LsqApiServiceListSource(
                    this._listInvoicePayLaterEligibleForBuyerService,
                    initialRequest
                );
            } else {
                this.tableDataSource.dispatchRequest(initialRequest);
            }
        }
    }

    private _wouldExceedLimit(invoiceAmount: Money): boolean {
        if (!this.facilityLimitAmount) {
            return true;
        }
        const pctUtilznForInvoice = (invoiceAmount.cents / this.facilityLimitAmount.cents) * 100;
        return (this.utilizedAmountPercentage ?? 0) + pctUtilznForInvoice > 100;
    }

    public toggleCheckbox(row: InvoiceSummary2): void {
        this.checkableRowsService.toggleCheck(row[this.rowPrimaryKey]);
    }

    public stopPropagation(event: Event) {
        event.stopPropagation();
    }

    public async setAllSelectableInvoices(
        req: ListInvoicePayLaterEligibleForBuyerRequest
    ): Promise<void> {
        this.checkableRowsService.setLoading(true);
        let selectAllData: InvoiceSummary2[] = [];
        if (this.isFinancingAccountPortalUser) {
            throw new Error('This feature is not yet implemented for account-level users');
        } else {
            selectAllData = await this._listInvoicePayLaterEligibleForBuyerService
                .executeOnce(
                    new ListInvoicePayLaterEligibleForBuyerRequest({
                        ...req,
                        pagination: {
                            take: TAKE_ALL
                        } as Pagination
                    })
                )
                .then(({ rows }) => rows.filter(this._isCheckable));
        }

        this.allSelectableInvoices$.next(selectAllData);
        const allRowKeys = selectAllData.map((invoice) => invoice.refInvoiceKey);
        this.checkableRowsService.init(allRowKeys);
        this.checkableRowsService.setCurrentAllFilteredRowKeys(allRowKeys);
        this.checkableRowsService.setLoading(false);
    }

    public setInvoiceKeysSelected(invoiceKeys: Guid[]): void {
        this._selectedInvoiceKeys$.next(invoiceKeys);
    }

    public disableConfirmationDetails(): boolean {
        return this.utilizedAmountPercentage > 100 || this.totalInvoices <= 0;
    }

    public setPageSelectableInvoices(invoices: InvoiceSummary2[]): void {
        const selectableInvoices = invoices.map((invoice) => new LoadableInvoiceSummary(invoice));
        this.pageSelectableInvoices$.next(selectableInvoices);
    }

    public trackByInvoiceKey(_index: number, { refInvoiceKey }: InvoiceSummary2): Guid {
        return refInvoiceKey;
    }

    public async handleInvoiceGroupToggleClick(row: InvoiceSummary2): Promise<void> {
        if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
            await this._collapseGroup(row.refInvoiceKey);
        } else {
            await this._expandGroup(row);
        }
    }

    public handleSelectAllChange(): void {
        if (!this.checkableRowsService.areAllRowsChecked()) {
            this.checkableRowsService.checkAll();
        } else {
            this.checkableRowsService.uncheckAll();
        }
    }

    public async setFilterQueryParams(): Promise<void> {
        const queryParams = {
            ...TableHelper.getQueryParamsByListSource(this.tableDataSource),
            [FastTrackRouteQueryParams.SearchText]:
                this.searchInvoicesFormGroup.controls.invoiceNumberSearch.value || undefined
        };
        await this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    public isInvoiceClickable(row: InvoiceSummary): boolean {
        return (
            (row.isGroup &&
                !row.refGroupedInvoiceKey &&
                this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)) ||
            this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountInvoiceDetails)
        );
    }

    private _openInvoiceDetail(invoice: InvoiceSummary): void {
        const isGroup = invoice.isGroup && !invoice.refGroupedInvoiceKey;

        const groupedInvoiceDetailRoute = this.isFinancingAccountPortalUser
            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL
            : SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL;

        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        isGroup ? groupedInvoiceDetailRoute : BUYER_ACCOUNT_INVOICE_DETAIL,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { invoiceKey: invoice.refInvoiceKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES
                    }
                }
            )
        );
    }

    private async _expandGroup(row: InvoiceSummary2): Promise<void> {
        if (this.invoiceGroupTogglesAreDisabled) {
            return;
        }
        this.invoiceGroupTogglesAreDisabled = true;
        if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
            await this._collapseGroup(row.refInvoiceKey);
        } else {
            await this._expandGroupByInvoiceKey(row.refInvoiceKey);
        }
        this.invoiceGroupTogglesAreDisabled = false;
    }

    private async _collapseGroup(refInvoiceKey: string): Promise<void> {
        this.expandedGroupInvoiceKeys.delete(refInvoiceKey);
        await this.tableDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);
    }

    private async _expandGroupByInvoiceKey(refInvoiceKey: string): Promise<void> {
        const canRevoke = this._featureFlagService.isOnSync(this.featureFlags.RevokeCreditMemo);
        // Collapse the group if it was somehow already open.
        if (this.expandedGroupInvoiceKeys.has(refInvoiceKey)) {
            await this._collapseGroup(refInvoiceKey);
        }

        // Make sure we're expanding a valid `refInvoiceKey`.
        let invoiceSummaryRows = await this.tableDataSource.rows$.pipe(first()).toPromise();
        let index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);
        if (index < 0) {
            return;
        }

        // Expand the row visually, and add the "loading" row.
        this.expandedGroupInvoiceKeys.add(refInvoiceKey);
        const loadingRow = new LoadableInvoiceSummary({
            isRowLoading: true,
            refInvoiceKey: refInvoiceKey,
            refGroupedInvoiceKey: 'loading' // So we get the grouped invoice styling
        });
        this.tableDataSource.expandGroup(index, [loadingRow]);

        // Get the grouped invoices.
        if (this.isFinancingAccountPortalUser) {
            throw new Error('This feature is not yet implemented for account-level users');
        } else {
            const {
                rows: groupedInvoices
            } = await this._listGroupedInvoicePayLaterEligibleForBuyerService.executeOnce(
                new ListGroupedInvoicePayLaterEligibleForBuyerRequest({
                    buyerKey: this._getAlternateCompanyKey(),
                    buyerInvoiceStatus: [
                        PayableStatus.APPROVED,
                        PayableStatus.EARLY_PAID,
                        PayableStatus.EARLY_PAY_REQUESTED
                    ],
                    refInvoiceKey: refInvoiceKey,
                    canRevoke
                })
            );

            // Collapse the loading row.
            await this.tableDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);

            // Expand the group for real.
            invoiceSummaryRows = await this.tableDataSource.rows$.pipe(first()).toPromise();
            index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);
            if (index < 0) {
                return;
            }
            this.tableDataSource.expandGroup(index, groupedInvoices);
        }
    }

    private _getAlternateCompanyKey(): string {
        return this._userService.getAlternateCompanyKey();
    }

    public rowIsUncheckable(row: InvoiceSummary2): boolean {
        // Nested invoices/memos should not be checkable.
        // Only flex term eligible invoices should be checkable.
        // An invoice that would exceed the credit limit should not be checkable.
        // A checked invoice should always be un-checkable.
        if (
            this.facilityLimitAmount == undefined ||
            this.utilizedAmount$ == undefined ||
            !this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortalEditInvoices)
        ) {
            return true;
        } else {
            return (
                !!row.refGroupedInvoiceKey ||
                (!this.checkableRowsService.isRowChecked(row.refInvoiceKey) &&
                    (!row.payLaterEligible || this._wouldExceedLimit(row.invoiceAmount)))
            );
        }
    }

    private _getRowClassName(row: InvoiceSummary): string {
        const classList = [];
        if (row.isGroup) {
            classList.push(ClassNames.IsGroup);

            if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
                classList.push(ClassNames.Expanded);
            }
        }
        if (row.refGroupedInvoiceKey) {
            classList.push(ClassNames.IsMemberOfGroup);
        }
        return classList.join(' ');
    }

    public async resetPage(): Promise<void> {
        this.tableDataSource.dispatchRequest({ ...this.tableDataSource.request });
        this.facilityUtilization = from(
            this._getFacilityUtilizationPayLater.executeOnce(
                new FacilityUtilizationLegacyRequest({
                    refBuyerKeys: [this._getAlternateCompanyKey()]
                })
            )
        );
        this.agree = false;
    }

    public convertExportRequest(
        request: ListInvoiceSummaryForSellerRequest
    ): GetSellerInvoiceExportRequest {
        return {
            ...request,
            invoiceExportType: InvoiceExportType.EARLY_PAYMENT,
            useMapStatus: false
        };
    }

    public async requestPayLater(): Promise<void> {
        const selectedInvoiceKeys = await this._selectedInvoiceKeys$.pipe(first()).toPromise();

        this.submittingBatch = true;

        this.requestPayLaterHandle(
            this._acceptOffer.executeOnce(
                new AcceptOfferRequest({
                    payablesRequest: selectedInvoiceKeys.map((payable) => {
                        return { id: Number(payable) } as PayableRequest;
                    }),
                    bankAccountKey: '',
                    effectiveFundingDate: this.today.toISOString().split('T')[0],
                    isPayLater: true
                })
            )
        );
    }

    private requestPayLaterHandle(promise: Promise<AcceptOfferResponse>) {
        promise
            .then((response) => {
                this.submittingBatch = false;
                const {
                    earlyPaidAmountTotal,
                    payableAmountTotal,
                    onTermsFundingAmountTotal
                } = response;
                const confirmationModal = new Modal({
                    title: this.paymentConfirmationTitle,
                    bodyComponent: RequestPayLaterConfirmationModalComponent,
                    showFooter: true,
                    width: '660px',
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    okButtonText: this.okButtonText,
                    cancelButtonText: this.backToPayLaterRequestButton,
                    clickOutsideToClose: false,
                    onOkClick: async () => {
                        this._modalService.closeModal();
                        await this._router.navigateByUrl(
                            getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER_PAYMENTS)
                        );
                    },
                    onCloseButtonClick: () => {
                        this._modalService.closeModal();
                        this.resetPage();
                    },
                    onCancelClick: () => {
                        this._modalService.closeModal();
                        this.resetPage();
                    },
                    data: {
                        totalInvoiceAmount: payableAmountTotal,
                        earlyPaymentAmount: earlyPaidAmountTotal,
                        totalPaymentAmount: onTermsFundingAmountTotal
                    }
                });
                this._modalService.showModal(confirmationModal);
            })
            .catch((error) => {
                console.error(error);
                this.submittingBatch = false;
                this._alertService.error(this._errorMessageHelper.GENERIC_ERROR);
            });
    }

    public getDueIn(dueIn: number): string {
        return dueIn < 0 ? '0' : `${dueIn}`;
    }

    public getValue(value: number): string {
        return value || value === 0 ? `${value}` : '----';
    }

    public clearFilters(): void {
        this.searchInvoicesFormGroup.reset();
        this.invoiceNumberSearchCmp.clear(); // FIXME: Shouldn't be necessary, resetting the group should clear the input.
        this.dateFilterFormGroup.reset();
        this.sellersFormGroup.reset();
        this._initTable();
    }

    private _isCheckable(invoice: InvoiceSummary2): boolean {
        return invoice.payLaterEligible;
    }
}
